import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  ListItemText,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import useAnalyticsWarehouses from "./useAnalyticsWarehouses";
import {
  normalizeWarehouseSelection,
  warehouseOptionLabel,
  warehouseSelectionSummary,
} from "../../utils/analyticsWarehouses";

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Warehouse picker for analytics pages. Options come from the analytics-only
 * endpoint, so a client's "Analytics Warehouses" configuration decides what
 * is offered; operational pickers are unaffected.
 *
 * mode="multi"  value: option[]   — [] means "All warehouses" (no filter,
 *                                   every warehouse of the client).
 * mode="single" value: option|null — null means "All warehouses" when
 *                                   `allowAll`, otherwise "nothing selected".
 *
 * Options load when the menu opens (server-side search + paging), so pages
 * never load every warehouse up front.
 */
const AnalyticsWarehousePicker = ({
  clientId,
  mode = "single",
  value,
  onChange,
  allowAll = mode === "multi",
  placeholder,
  disabled = false,
  width = 220,
  height = 38,
  triggerSx,
  ariaLabel = "Warehouse",
  testId = "analytics-warehouse-picker",
}) => {
  const multi = mode === "multi";
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const open = Boolean(anchorEl);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { options, loading, error, hasMore, loadMore, retry, configured, total } =
    useAnalyticsWarehouses(clientId, { search, enabled: open });

  const selected = useMemo(
    () => (multi ? normalizeWarehouseSelection(value) : value ? [value] : []),
    [multi, value],
  );
  const selectedIds = useMemo(
    () => new Set(selected.map((w) => String(w._id))),
    [selected],
  );

  const allLabel = "All warehouses";
  const emptyLabel = allowAll ? allLabel : placeholder || "Select a warehouse";
  const summary = multi
    ? warehouseSelectionSummary(selected, { allLabel })
    : value
      ? warehouseOptionLabel(value)
      : emptyLabel;
  const tooltip =
    multi && selected.length > 1 ? (
      <Box component="span" sx={{ display: "block" }}>
        {selected.map((w) => (
          <Box component="span" key={w._id} sx={{ display: "block" }}>
            {warehouseOptionLabel(w)}
          </Box>
        ))}
      </Box>
    ) : (
      ""
    );

  const close = () => {
    setAnchorEl(null);
    setSearchInput("");
    setSearch("");
  };

  const choose = (option) => {
    if (!multi) {
      onChange(option || null);
      close();
      return;
    }
    if (!option) {
      onChange([]);
      return;
    }
    const id = String(option._id);
    const next = selectedIds.has(id)
      ? selected.filter((w) => String(w._id) !== id)
      : [...selected, option];
    onChange(normalizeWarehouseSelection(next));
  };

  const onListScroll = (event) => {
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) loadMore();
  };

  const hasSelection = selected.length > 0;
  const showAllRow = allowAll && !search;

  return (
    <>
      <Tooltip title={tooltip} arrow placement="bottom-start">
        <span style={{ display: "inline-flex", width }}>
          <Button
            variant="outlined"
            disabled={disabled}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 20 }} />}
            aria-haspopup="listbox"
            aria-expanded={open ? "true" : undefined}
            aria-label={`${ariaLabel}: ${summary}`}
            data-testid={testId}
            sx={{
              width: "100%",
              height,
              justifyContent: "space-between",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 500,
              px: 1.5,
              borderColor: "var(--border-color)",
              backgroundColor: "var(--bg-paper)",
              color: hasSelection ? "var(--text-primary)" : "var(--text-secondary)",
              "&:hover": {
                borderColor: "var(--text-tertiary)",
                backgroundColor: "var(--bg-paper)",
              },
              ...triggerSx,
            }}
          >
            <Box
              component="span"
              sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {summary}
            </Box>
          </Button>
        </span>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: { width: typeof width === "number" ? Math.max(280, width) : 320, mt: 0.5 },
        }}
      >
        <Box sx={{ px: 1.25, pt: 1, pb: 0.75 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search name or code…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            inputProps={{ "aria-label": "Search warehouses" }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ fontSize: 16, color: "var(--text-tertiary)", mr: 0.5 }} />
              ),
            }}
            sx={{ "& .MuiInputBase-root": { fontSize: 13 } }}
          />
        </Box>
        {multi && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 1.5,
              pb: 0.5,
            }}
          >
            <Typography sx={{ fontSize: 12, color: "var(--text-tertiary)" }}>
              {hasSelection ? `${selected.length} selected` : "No filter applied"}
            </Typography>
            <Button
              size="small"
              disabled={!hasSelection}
              onClick={() => onChange([])}
              sx={{ textTransform: "none", fontSize: 12, minWidth: 0, px: 0.5 }}
            >
              Clear
            </Button>
          </Box>
        )}
        <Divider />
        <Box
          role="listbox"
          aria-multiselectable={multi ? "true" : undefined}
          aria-label={ariaLabel}
          onScroll={onListScroll}
          sx={{ maxHeight: 300, overflowY: "auto", py: 0.5 }}
        >
          {showAllRow && (
            <>
              <MenuItem
                dense
                role="option"
                aria-selected={!hasSelection}
                selected={!hasSelection}
                onClick={() => choose(null)}
                sx={{ fontSize: 13, py: 0.6 }}
              >
                {multi ? (
                  <Checkbox size="small" checked={!hasSelection} sx={{ p: 0.5, mr: 0.5 }} />
                ) : (
                  <CheckIcon
                    sx={{ fontSize: 16, mr: 1, visibility: hasSelection ? "hidden" : "visible" }}
                  />
                )}
                <ListItemText
                  primary={allLabel}
                  secondary="Every warehouse for this client"
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: 11 }}
                />
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
            </>
          )}
          {options.map((option) => {
            const isSelected = selectedIds.has(String(option._id));
            return (
              <MenuItem
                key={option._id}
                dense
                role="option"
                aria-selected={isSelected}
                selected={!multi && isSelected}
                onClick={() => choose(option)}
                sx={{ fontSize: 13, py: 0.4 }}
              >
                {multi ? (
                  <Checkbox size="small" checked={isSelected} sx={{ p: 0.5, mr: 0.5 }} />
                ) : (
                  <CheckIcon
                    sx={{ fontSize: 16, mr: 1, visibility: isSelected ? "visible" : "hidden" }}
                  />
                )}
                <ListItemText
                  primary={warehouseOptionLabel(option)}
                  primaryTypographyProps={{ fontSize: 13, noWrap: true }}
                />
              </MenuItem>
            );
          })}
          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25 }}>
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: 12.5, color: "var(--text-tertiary)" }}>
                Loading warehouses…
              </Typography>
            </Box>
          )}
          {!loading && error && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1 }}>
              <Typography sx={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
                {error}
              </Typography>
              <Button size="small" onClick={retry} sx={{ textTransform: "none", fontSize: 12 }}>
                Retry
              </Button>
            </Box>
          )}
          {!loading && !error && total === 0 && (
            <Typography sx={{ px: 2, py: 1.25, fontSize: 12.5, color: "var(--text-tertiary)" }}>
              {search ? `No warehouses match "${search}"` : "No warehouses available"}
            </Typography>
          )}
          {!loading && !error && hasMore && (
            <Box sx={{ px: 1.25, py: 0.5 }}>
              <Button
                size="small"
                fullWidth
                onClick={loadMore}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                Load more
              </Button>
            </Box>
          )}
        </Box>
        {configured && (
          <>
            <Divider />
            <Typography sx={{ px: 1.5, py: 0.75, fontSize: 11, color: "var(--text-tertiary)" }}>
              Showing this client&apos;s analytics warehouses.
            </Typography>
          </>
        )}
      </Popover>
    </>
  );
};

export default AnalyticsWarehousePicker;
